<template>
  <div class="exercises-item" :class="{ 'isMobile': isMobile }">
    <div class="title-section">
      <div class="exercises-title">
        <el-tooltip ref="collectTooltip" class="item" effect="dark" placement="top" :content="question.isCollected ? '取消收藏' : '收藏'">
          <div class="collect-question">
            <i v-show="!question.isCollected" class="el-icon-star-off" style="color: #9dca67"
              @click="collectQuestion(question, 'collectTooltip')"></i>
            <i v-show="question.isCollected" class="el-icon-star-on" style="color: #9dca67"
              @click="removeCollect(question, 'collectTooltip')"></i>
          </div>
        </el-tooltip>
        <span class="serial-number">{{ showIndex + 1 }}.</span>
        <div class="question" :id="question.nanoid + '_title'" v-html="question.question"></div>
      </div>
    </div>

    <div v-if="!isChooseType(question)">
      <div class="user-answer" v-show="question.userAnswer">
        <div class="user-answer-header">
          <span class="placeholder"></span>
          <div class="title">我的答案</div>
          <i class="el-icon-delete delete-answer" @click.stop="clearUserAnswer(question)"></i>
        </div>
        <!-- 如果是 base64 图片答案，用原生 img 标签渲染，避免 markdown-render 发送错误请求 -->
        <div v-if="isImageAnswer(question.userAnswer)">
          <img :src="extractImageSrc(question.userAnswer)" style="max-width: 100%;" />
        </div>
        <markdown-render v-else :markdown="question.userAnswer"> </markdown-render>
      </div>
    </div>
    <div class="option-wrap flex-horizon" v-if="question.question_type === 'SCQ'">
      <el-radio-group class="group" v-model="question.userAnswer">
        <el-radio v-for="(item, index) in question.option" :label="options[index]">
          <div class="custom-label" v-html="question.option[index]"></div>
        </el-radio>
      </el-radio-group>
    </div>
    <div class="option-wrap flex-horizon" v-else-if="question.question_type === 'MCQ'">
      <el-checkbox-group class="group" v-model="question.userAnswer">
        <el-checkbox v-for="(item, index) in question.option" :label="options[index]">
          <div class="custom-label" v-html="question.option[index]"></div>
        </el-checkbox>
      </el-checkbox-group>
    </div>
    <div class="option-wrap flex-horizon" v-else-if="question.question_type === 'TF'">
      <el-radio-group v-model="question.userAnswer">
        <el-radio value="true" label="√"></el-radio>
        <el-radio value="false" label="×"></el-radio>
      </el-radio-group>
    </div>
    <slot name="operation"></slot>
    <div v-show="question.isAnswered" class="collapse-wrapper">
      <el-collapse v-model="question.isStandardEpxlCollapse" @change="handleCollapseChange">
        <el-collapse-item title="答案分析" :name="question.nanoid + 'answer_expl'">
          <div class="standard-answer" :id="question.nanoid + 'answer_expl'">
            <markdown-render v-for="(value, key) in question.standardAnswer" :markdown="value"
              :key="'standardAnswer' + key"></markdown-render>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>

    <div v-show="question.isView" class="collapse-wrapper">
      <el-collapse v-model="question.isViweAnswerCollapse">
        <el-collapse-item title="查看答案" :name="question.nanoid + 'view_answer'">
          <div v-show="question.viewAnswer" class="view-answer" :id="question.nanoid + 'view_answer'">
            <markdown-render v-for="(value, key) in question.viewAnswer" :markdown="value"
              :key="'viewAnswer' + key"></markdown-render>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>
    <div v-show="question.teacher_expl" class="collapse-wrapper">
      <el-collapse v-model="question.isTeacherEpxlCollapse">
        <el-collapse-item :name="question.nanoid + 'teacher_expl'">
          <!-- 自定义标题，增加全屏按钮及提示 -->
          <template slot="title">
            <span>老师讲解</span>
            <el-tooltip ref="teacherFullscreenTooltip" class="item" effect="dark" placement="top" content="全屏查看">
              <i class="el-icon-full-screen fullscreen-teacher-icon" @click.stop="openFullscreenTeacher"></i>
            </el-tooltip>
          </template>
          <div class="teacher-expl">
            <markdown-render :key="question.nanoid + 'teacher_expl'" :markdown="question.teacher_expl"></markdown-render>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>

    <!-- 答题弹窗 - 自定义标题增加拍照按钮 -->
    <el-dialog :visible.sync="question.showAnserPanel" :append-to-body="true" class="answerDialog"
      :class="{ 'is-fullscreen': isEditorFullscreen }" v-dialogDrag ref="answerDialogRef" :show-close="false">
      <div slot="title" class="answer-dialog-title">
        <span>我要答题</span>
        <button class="photo-upload-btn" @click="handlePhotoUploadForAnswer">
          📷 拍照上传答案
        </button>
      </div>
      <answer-panel :answerHtml="question.answerHtml" v-if="question.showAnserPanel" ref="answerPanelRef"
        @fullscreen-change="handleEditorFullscreen" />
      <div slot="footer" class="dialog-footer base-button">
        <button @click="question.showAnserPanel = false" style="margin-right: 10px">取 消</button>
        <button type="primary" @click="sendEditorAnswer(question, chat)">确 定</button>
      </div>
    </el-dialog>

    <!-- 草稿纸弹窗 -->
    <el-dialog v-dialogDrag width="50%" top="21vh" class="roughPaperDialog"
      :class="{ 'is-fullscreen': roughPaperFullscreen }" :key="question.nanoid + 'rough'"
      :close-on-click-modal="false" :visible.sync="question.roughPaperShow" :append-to-body="true" :modal="false"
      ref="roughPaperDialogRef">
      <span slot="title" class="rough-paper-title">
        <i class="el-icon-edit-outline draft-icon"></i>
        <span>草稿纸</span>
      </span>
      <draw-panel class="draw-panel" :fullscreen.sync="roughPaperFullscreen" :storage-key="'drawpad_' + question.nanoid"></draw-panel>
      <div slot="footer" class="dialog-footer base-button">
        <button @click="question.roughPaperShow = false" style="margin-right: 10px">取 消</button>
      </div>
    </el-dialog>

    <!-- 老师讲解全屏弹窗（左右分屏，可拖动分割线） -->
    <el-dialog
      :visible.sync="fullscreenTeacherVisible"
      fullscreen
      :append-to-body="true"
      class="teacher-split-dialog"
      :show-close="true"
      :close-on-click-modal="false"
      @opened="onSplitDialogOpened"
      @closed="onSplitDialogClosed">
      <!-- 修改标题：在“老师讲解”左侧添加草稿纸图标 -->
      <div slot="title" class="split-dialog-title">
        <div class="title-left">
          <el-tooltip ref="fullscreenRoughTooltip" class="item" effect="dark" placement="top" content="草稿纸">
            <i class="el-icon-edit-outline toolbar-icon" @click="openRoughPaperInFullscreen"></i>
          </el-tooltip>
          <span>老师讲解</span>
          <!-- 新增恢复默认布局按钮，始终显示 -->
          <el-tooltip ref="resetLayoutTooltip" class="item" effect="dark" placement="top" content="恢复默认布局">
            <i class="el-icon-refresh toolbar-icon" @click="resetSplitLayout"></i>
          </el-tooltip>
        </div>
      </div>
      <!-- 使用 Grid 布局，解决宽度溢出问题并支持拖拽 -->
      <div class="split-container" ref="splitContainer" :style="{ gridTemplateColumns: `${leftPanelWidth}% 6px 1fr` }">
        <!-- 左侧面板 -->
        <div class="left-panel">
          <div class="exercises-item" :class="{ 'isMobile': isMobile }">
            <!-- 标题区（序号、收藏、题干） -->
            <div class="title-section">
              <div class="exercises-title">
                <el-tooltip ref="fullscreenCollectTooltip" class="item" effect="dark" placement="top" :content="question.isCollected ? '取消收藏' : '收藏'">
                  <div class="collect-question">
                    <i v-show="!question.isCollected" class="el-icon-star-off" style="color: #9dca67" @click="collectQuestion(question, 'fullscreenCollectTooltip')"></i>
                    <i v-show="question.isCollected" class="el-icon-star-on" style="color: #9dca67" @click="removeCollect(question, 'fullscreenCollectTooltip')"></i>
                  </div>
                </el-tooltip>
                <span class="serial-number">{{ showIndex + 1 }}.</span>
                <div class="question" :id="question.nanoid + '_title'" v-html="question.question"></div>
              </div>
            </div>

            <!-- 非选择题型：显示用户答案 -->
            <div v-if="!isChooseType(question)">
              <div class="user-answer" v-show="question.userAnswer">
                <div class="user-answer-header">
                  <span class="placeholder"></span>
                  <div class="title">我的答案</div>
                  <i class="el-icon-delete delete-answer" @click.stop="clearUserAnswer(question)"></i>
                </div>
                <div v-if="isImageAnswer(question.userAnswer)">
                  <img :src="extractImageSrc(question.userAnswer)" style="max-width: 100%;" />
                </div>
                <markdown-render v-else :markdown="question.userAnswer"></markdown-render>
              </div>
            </div>

            <!-- 单选题选项 -->
            <div class="option-wrap flex-horizon" v-if="question.question_type === 'SCQ'">
              <el-radio-group class="group" v-model="question.userAnswer">
                <el-radio v-for="(item, index) in question.option" :label="options[index]">
                  <div class="custom-label" v-html="question.option[index]"></div>
                </el-radio>
              </el-radio-group>
            </div>

            <!-- 多选题选项 -->
            <div class="option-wrap flex-horizon" v-else-if="question.question_type === 'MCQ'">
              <el-checkbox-group class="group" v-model="question.userAnswer">
                <el-checkbox v-for="(item, index) in question.option" :label="options[index]">
                  <div class="custom-label" v-html="question.option[index]"></div>
                </el-checkbox>
              </el-checkbox-group>
            </div>

            <!-- 判断题选项 -->
            <div class="option-wrap flex-horizon" v-else-if="question.question_type === 'TF'">
              <el-radio-group v-model="question.userAnswer">
                <el-radio value="true" label="√"></el-radio>
                <el-radio value="false" label="×"></el-radio>
              </el-radio-group>
            </div>

            <!-- 答案分析折叠面板（已提交答案后显示） -->
            <div v-show="question.isAnswered" class="collapse-wrapper">
              <el-collapse v-model="question.isStandardEpxlCollapse" @change="handleCollapseChange">
                <el-collapse-item title="答案分析" :name="question.nanoid + 'answer_expl'">
                  <div class="standard-answer" :id="question.nanoid + 'answer_expl'">
                    <markdown-render v-for="(value, key) in question.standardAnswer" :markdown="value" :key="'standardAnswer' + key"></markdown-render>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>
          </div>
        </div>

        <!-- 可拖动分割线 -->
        <div class="resize-handle" @mousedown="startResizeSplit" @touchstart="startResizeSplit"></div>

        <!-- 右侧面板 -->
        <div class="right-panel">
          <div class="teacher-content">
            <markdown-render :markdown="question.teacher_expl || '暂无老师讲解内容'" :key="'fullscreen_teacher_' + question.nanoid"></markdown-render>
          </div>
        </div>
      </div>

      <!-- 底部 footer -->
    <div slot="footer" class="dialog-footer base-button teacher-footer">
      <button class="fullscreen-close-btn" @click="fullscreenTeacherVisible = false">关闭</button>
    </div>
    </el-dialog>

    <!-- 移动端动作面板 -->
    <div v-if="showActionSheet" class="action-sheet-mask" @click.self="showActionSheet = false">
      <div class="action-sheet">
        <div class="action-sheet-item" @click="chooseCameraForAnswer">📸 拍照上传答案</div>
        <div class="action-sheet-item" @click="chooseAlbumForAnswer">🖼️ 从相册上传答案</div>
        <div class="action-sheet-item cancel" @click="showActionSheet = false">❌ 取消</div>
      </div>
    </div>

    <!-- 自定义相机取景框 -->
    <div v-if="showCamera" class="camera-overlay" @click.self="closeCameraForAnswer">
      <div class="camera-container" ref="cameraContainer">
        <video ref="cameraVideo" class="camera-video" autoplay playsinline></video>
        <div class="crop-box" :style="cropBoxStyle" @touchstart="startDrag" @touchmove.prevent="onTouchMove" @touchend="onTouchEnd">
          <div class="crop-area">
            <div class="crop-hint">可拖拉取景框<br/>平行纸面拍照<br/>题目放入框内</div>
          </div>
          <div class="resize-handle" @touchstart="startResize($event)" @touchmove.prevent="onTouchMove" @touchend="onTouchEnd"></div>
        </div>
        <div class="camera-footer">
          <button class="btn-cancel" @click="closeCameraForAnswer">取消</button>
          <button class="btn-capture" @click="takePhotoForAnswer">拍照</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  collectQuestion,
  removeCollect,
} from "@/api/chat/sendReceive";
import MarkdownRender from "@/components/MarkdownRender.vue";
import answerPanel from "@/components/question/answerPanel.vue";
import { mapState } from "vuex/dist/vuex.common.js";
import drawPanel from "@/components/draw/index.vue";
import store from "../../store";

export default {
  name: "Question",
  data() {
    return {
      options: ["A", "B", "C", "D"],
      isEditorFullscreen: false,
      roughPaperFullscreen: false,
      savedDialogOffset: { top: '', left: '', transform: '' },
      savedAnswerDialogOffset: { top: '', left: '', transform: '' },
      // 拍照上传相关状态
      showActionSheet: false,
      showCamera: false,
      cameraStream: null,
      videoWidth: 0,
      videoHeight: 0,
      cropBox: {
        left: 0.01,
        top: 0.25,
        width: 0.98,
        height: 0.3,
      },
      isDragging: false,
      isResizing: false,
      dragStartData: {},
      // 老师讲解全屏弹窗控制
      fullscreenTeacherVisible: false,
      // 左右分屏宽度百分比（左侧）
      leftPanelWidth: 30,
      // 拖动分割线相关
      isResizingSplit: false,
      startX: 0,
      startWidth: 0,
      // 用于 requestAnimationFrame 节流
      rafId: null,
      // 所有 tooltip ref 列表（用于移动端自动关闭）
      tooltipRefs: [
        'collectTooltip',
        'teacherFullscreenTooltip',
        'fullscreenCollectTooltip',
        'fullscreenRoughTooltip',
        'resetLayoutTooltip'
      ]
    };
  },
  props: ["chat", "question", "showIndex", "robotId", "index", "hideToolbar"],
  computed: {
    ...mapState(["isMobile"]),
    cropBoxStyle() {
      return {
        left: this.cropBox.left * 100 + '%',
        top: this.cropBox.top * 100 + '%',
        width: this.cropBox.width * 100 + '%',
        height: this.cropBox.height * 100 + '%',
      };
    },
  },
  components: {
    MarkdownRender,
    answerPanel,
    drawPanel,
  },
  watch: {
    'question.showAnserPanel'(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const dialogEl = this.$refs.answerDialogRef?.$el?.querySelector('.el-dialog');
          if (dialogEl) {
            dialogEl.style.top = '';
            dialogEl.style.left = '';
            dialogEl.style.transform = '';
          }
          if (this.$refs.answerPanelRef && this.$refs.answerPanelRef.resizeEditor) {
            this.$refs.answerPanelRef.resizeEditor();
          }
        });
      } else {
        this.isEditorFullscreen = false;
      }
    },
    roughPaperFullscreen(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const dialogEl = this.$refs.roughPaperDialogRef?.$el?.querySelector('.el-dialog');
          if (dialogEl) {
            this.savedDialogOffset = {
              top: dialogEl.style.top || '',
              left: dialogEl.style.left || '',
              transform: dialogEl.style.transform || ''
            };
            dialogEl.style.top = '';
            dialogEl.style.left = '';
            dialogEl.style.transform = '';
          }
        });
      } else {
        this.$nextTick(() => {
          const dialogEl = this.$refs.roughPaperDialogRef?.$el?.querySelector('.el-dialog');
          if (dialogEl && (this.savedDialogOffset.top || this.savedDialogOffset.left || this.savedDialogOffset.transform)) {
            dialogEl.style.top = this.savedDialogOffset.top;
            dialogEl.style.left = this.savedDialogOffset.left;
            dialogEl.style.transform = this.savedDialogOffset.transform;
          }
          this.savedDialogOffset = { top: '', left: '', transform: '' };
        });
      }
    },
    isEditorFullscreen(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          const dialogEl = this.$refs.answerDialogRef?.$el?.querySelector('.el-dialog');
          if (dialogEl) {
            this.savedAnswerDialogOffset = {
              top: dialogEl.style.top || '',
              left: dialogEl.style.left || '',
              transform: dialogEl.style.transform || ''
            };
            dialogEl.style.top = '';
            dialogEl.style.left = '';
            dialogEl.style.transform = '';
          }
        });
      } else {
        this.$nextTick(() => {
          const dialogEl = this.$refs.answerDialogRef?.$el?.querySelector('.el-dialog');
          if (dialogEl && (this.savedAnswerDialogOffset.top || this.savedAnswerDialogOffset.left || this.savedAnswerDialogOffset.transform)) {
            dialogEl.style.top = this.savedAnswerDialogOffset.top;
            dialogEl.style.left = this.savedAnswerDialogOffset.left;
            dialogEl.style.transform = this.savedAnswerDialogOffset.transform;
          }
          this.savedAnswerDialogOffset = { top: '', left: '', transform: '' };
        });
      }
    },
    'question.roughPaperShow'(newVal) {
      if (newVal) {
        this.roughPaperFullscreen = true;
      } else {
        this.roughPaperFullscreen = false;
      }
    }
  },
  beforeDestroy() {
    this.removeTouchListeners();
    this.removeResizeListeners();
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  },
  mounted() { },
  methods: {
    // 移动端自动显示 tooltip 并在延迟后关闭
    autoShowTooltip(refName, delay = 1200) {
      if (!this.isMobile) return;
      const tooltip = this.$refs[refName];
      if (tooltip && typeof tooltip.handleShow === 'function') {
        tooltip.handleShow();
        setTimeout(() => {
          if (tooltip && typeof tooltip.handleClose === 'function') {
            tooltip.handleClose();
          }
        }, delay);
      }
    },
    // 关闭所有 tooltip（移动端可选，用于清理残留）
    closeAllTooltips() {
      if (!this.isMobile) return;
      this.tooltipRefs.forEach(refName => {
        const tooltip = this.$refs[refName];
        if (tooltip && typeof tooltip.handleClose === 'function') {
          tooltip.handleClose();
        }
      });
    },
    // 移动端关闭指定 ref 列表的 tooltip（保留兼容旧代码）
    closeTooltipRefs(refNames) {
      if (!this.isMobile) return;
      refNames.forEach(refName => {
        const tooltip = this.$refs[refName];
        if (tooltip && typeof tooltip.handleClose === 'function') {
          tooltip.handleClose();
        }
      });
    },

    resetSplitLayout() {
      if (this.isMobile) {
        this.autoShowTooltip('resetLayoutTooltip', 1200);
        // 关闭其他可能残留的 tooltip
        this.tooltipRefs.forEach(ref => {
          if (ref !== 'resetLayoutTooltip') {
            const t = this.$refs[ref];
            t?.handleClose?.();
          }
        });
      }
      if (this.isResizingSplit) {
        this.stopResizeSplit();
      }
      this.leftPanelWidth = 30;
    },
    handleContentResize() { },
    async collectQuestion(question, tooltipRef = 'collectTooltip') {
      if (this.isMobile && tooltipRef) {
        this.autoShowTooltip(tooltipRef, 1200);
        // 关闭其他 tooltip，避免多个同时显示
        this.tooltipRefs.forEach(ref => {
          if (ref !== tooltipRef) {
            const t = this.$refs[ref];
            t?.handleClose?.();
          }
        });
      }
      let res = await collectQuestion({ id: question.id });
      let flag = 200 <= res.status && res.status < 300;
      if (!res || !flag) {
        this.$message.error("收藏失败");
        return;
      }
      if (flag) {
        question.isCollected = true;
        this.$message.success("收藏成功");
      }
    },
    async removeCollect(question, tooltipRef = 'collectTooltip') {
      if (this.isMobile && tooltipRef) {
        this.autoShowTooltip(tooltipRef, 1200);
        this.tooltipRefs.forEach(ref => {
          if (ref !== tooltipRef) {
            const t = this.$refs[ref];
            t?.handleClose?.();
          }
        });
      }
      let res = await removeCollect({ id: question.id });
      let flag = 200 <= res.status && res.status < 300;
      if (!res || !flag) {
        this.$message.error("取消失败");
        return;
      }
      if (flag) {
        this.$set(question, "isCollected", false);
        this.$message.success("已取消收藏");
      }
    },
    handleCollapseChange() { },
    isChooseType(question) {
      if (["SQ", "FIB"].includes(question.question_type)) {
        return false;
      } else {
        return true;
      }
    },
    handleEditorFullscreen(isFull) {
      this.isEditorFullscreen = isFull;
      this.$nextTick(() => {
        if (this.$refs.answerPanelRef && this.$refs.answerPanelRef.resizeEditor) {
          this.$refs.answerPanelRef.resizeEditor();
        }
      });
    },
    // 提交答案（直接使用已存储的 base64）
    sendEditorAnswer(question, chat) {
      if (this.isMobile) {
        this.closeAllTooltips(); // 清理任何残留 tooltip
      }
      question.level = this.chat.level;
      this.$emit("sendEditorAnswer", question);
      store.dispatch("updateMessageToConversation", { message: chat });
    },

    // ==================== 拍照上传答案（直接转 base64） ====================
    handlePhotoUploadForAnswer() {
      if (this.isMobile) {
        this.closeAllTooltips();
        this.question.showAnserPanel = false;
        this.showActionSheet = true;
      } else {
        this.triggerFileUploadForAnswer();
      }
    },

    triggerFileUploadForAnswer() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/jpg';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          await this.uploadAndSetAnswer(file);
        }
      };
      input.click();
    },

    chooseCameraForAnswer() {
      this.showActionSheet = false;
      this.openCustomCameraForAnswer();
    },

    chooseAlbumForAnswer() {
      this.showActionSheet = false;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          await this.uploadAndSetAnswer(file);
        }
      };
      input.click();
    },

    async openCustomCameraForAnswer() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
        });
        this.cameraStream = stream;
        this.showCamera = true;
        this.$nextTick(() => {
          const video = this.$refs.cameraVideo;
          video.srcObject = stream;
          video.play();
          video.onloadedmetadata = () => {
            this.videoWidth = video.videoWidth;
            this.videoHeight = video.videoHeight;
          };
        });
      } catch (err) {
        this.$message.error('无法访问摄像头，请检查权限');
        console.error(err);
      }
    },

    closeCameraForAnswer() {
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach(track => track.stop());
        this.cameraStream = null;
      }
      this.showCamera = false;
      this.removeTouchListeners();
    },

    async takePhotoForAnswer() {
      const video = this.$refs.cameraVideo;
      if (!video || !this.videoWidth || !this.videoHeight) return;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const container = this.$refs.cameraContainer;
      const videoRect = video.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const videoDisplayWidth = videoRect.width;
      const videoDisplayHeight = videoRect.height;
      const videoStreamRatio = this.videoWidth / this.videoHeight;
      const videoDisplayRatio = videoDisplayWidth / videoDisplayHeight;
      let renderedVideoWidth, renderedVideoHeight, renderedVideoOffsetX, renderedVideoOffsetY;
      if (videoDisplayRatio > videoStreamRatio) {
        renderedVideoHeight = videoDisplayHeight;
        renderedVideoWidth = renderedVideoHeight * videoStreamRatio;
        renderedVideoOffsetX = (videoDisplayWidth - renderedVideoWidth) / 2;
        renderedVideoOffsetY = 0;
      } else {
        renderedVideoWidth = videoDisplayWidth;
        renderedVideoHeight = renderedVideoWidth / videoStreamRatio;
        renderedVideoOffsetX = 0;
        renderedVideoOffsetY = (videoDisplayHeight - renderedVideoHeight) / 2;
      }
      const cropBoxScreenLeft = containerRect.left + (this.cropBox.left * containerRect.width);
      const cropBoxScreenTop = containerRect.top + (this.cropBox.top * containerRect.height);
      const cropBoxScreenWidth = this.cropBox.width * containerRect.width;
      const cropBoxScreenHeight = this.cropBox.height * containerRect.height;
      const cropBoxVideoElementLeft = (cropBoxScreenLeft - videoRect.left - renderedVideoOffsetX);
      const cropBoxVideoElementTop = (cropBoxScreenTop - videoRect.top - renderedVideoOffsetY);
      const scaleX = this.videoWidth / renderedVideoWidth;
      const scaleY = this.videoHeight / renderedVideoHeight;
      const cropX = cropBoxVideoElementLeft * scaleX;
      const cropY = cropBoxVideoElementTop * scaleY;
      const cropWidth = cropBoxScreenWidth * scaleX;
      const cropHeight = cropBoxScreenHeight * scaleY;
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      ctx.drawImage(video, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
      canvas.toBlob(async (blob) => {
        const fileName = `camera_${Date.now()}.jpg`;
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        await this.uploadAndSetAnswer(file);
        this.closeCameraForAnswer();
      }, 'image/jpeg', 0.9);
    },

    // 直接转为 base64 存储
    async uploadAndSetAnswer(file) {
      try {
        const base64 = await this.fileToBase64(file);
        const markdownImg = `![${file.name}](${base64})`;
        this.$set(this.question, 'userAnswer', markdownImg);
        this.question.showAnserPanel = false;
        this.$message.success('图片已设为答案，请点击“提交答案”按钮提交');
      } catch (error) {
        console.error(error);
        this.$message.error('图片处理失败');
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    // 辅助方法：判断答案是否为 base64 图片
    isImageAnswer(answer) {
      return answer && answer.startsWith('![') && answer.includes('data:image');
    },

    // 辅助方法：从 markdown 格式中提取图片 src
    extractImageSrc(answer) {
      const match = answer.match(/!\[.*?\]\((.*?)\)/);
      return match ? match[1] : '';
    },

    // 拖动取景框相关方法（相机内）
    startDrag(e) {
      e.preventDefault();
      if (this.isResizing) return;
      const container = this.$refs.cameraContainer;
      const rect = container.getBoundingClientRect();
      const touch = e.touches[0];
      this.dragStartData = {
        startX: touch.pageX,
        startY: touch.pageY,
        startLeft: this.cropBox.left,
        startTop: this.cropBox.top,
        startWidth: this.cropBox.width,
        startHeight: this.cropBox.height,
        containerWidth: rect.width,
        containerHeight: rect.height,
      };
      this.isDragging = true;
      this.addTouchListeners();
    },

    startResize(e) {
      e.preventDefault();
      e.stopPropagation();
      if (this.isDragging) return;
      const container = this.$refs.cameraContainer;
      const rect = container.getBoundingClientRect();
      const touch = e.touches[0];
      this.dragStartData = {
        startX: touch.pageX,
        startY: touch.pageY,
        startLeft: this.cropBox.left,
        startTop: this.cropBox.top,
        startWidth: this.cropBox.width,
        startHeight: this.cropBox.height,
        containerWidth: rect.width,
        containerHeight: rect.height,
      };
      this.isResizing = true;
      this.addTouchListeners();
    },

    onTouchMove(e) {
      e.preventDefault();
      if (!this.isDragging && !this.isResizing) return;
      const touch = e.touches[0];
      const { startX, startY, startLeft, startTop, startWidth, startHeight, containerWidth, containerHeight } = this.dragStartData;
      const deltaX = touch.pageX - startX;
      const deltaY = touch.pageY - startY;
      const deltaXPercent = deltaX / containerWidth;
      const deltaYPercent = deltaY / containerHeight;
      if (this.isDragging) {
        let newLeft = startLeft + deltaXPercent;
        let newTop = startTop + deltaYPercent;
        newLeft = Math.max(0, Math.min(1 - startWidth, newLeft));
        newTop = Math.max(0, Math.min(1 - startHeight, newTop));
        this.cropBox.left = newLeft;
        this.cropBox.top = newTop;
      } else if (this.isResizing) {
        let newWidth = startWidth + deltaXPercent;
        let newHeight = startHeight + deltaYPercent;
        newWidth = Math.max(0.1, Math.min(1 - startLeft, newWidth));
        newHeight = Math.max(0.1, Math.min(1 - startTop, newHeight));
        this.cropBox.width = newWidth;
        this.cropBox.height = newHeight;
      }
    },

    onTouchEnd(e) {
      e.preventDefault();
      this.isDragging = false;
      this.isResizing = false;
      this.removeTouchListeners();
    },

    addTouchListeners() {
      document.addEventListener('touchmove', this.onTouchMove, { passive: false });
      document.addEventListener('touchend', this.onTouchEnd);
      document.addEventListener('touchcancel', this.onTouchEnd);
    },

    removeTouchListeners() {
      document.removeEventListener('touchmove', this.onTouchMove);
      document.removeEventListener('touchend', this.onTouchEnd);
      document.removeEventListener('touchcancel', this.onTouchEnd);
    },

    // 清空用户答案
    clearUserAnswer(question) {
      if (this.isMobile) {
        this.closeAllTooltips();
      }
      this.$confirm('确定要清除当前答案吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$set(question, 'userAnswer', '');
        this.$set(question, 'isAnswered', false);
        // 关闭答案分析折叠面板（如果已展开）
        if (question.isStandardEpxlCollapse) {
          this.$set(question, 'isStandardEpxlCollapse', false);
        }
        this.$message.success('已清除答案');
      }).catch(() => {});
    },

    // 打开老师讲解全屏弹窗
    openFullscreenTeacher() {
      if (this.isMobile) {
        this.autoShowTooltip('teacherFullscreenTooltip', 1200);
        this.tooltipRefs.forEach(ref => {
          if (ref !== 'teacherFullscreenTooltip') {
            const t = this.$refs[ref];
            t?.handleClose?.();
          }
        });
      }
      if (this.question.teacher_expl) {
        this.fullscreenTeacherVisible = true;
      } else {
        this.$message.warning('暂无老师讲解内容');
      }
    },

    // 全屏模式下打开草稿纸
    openRoughPaperInFullscreen() {
      if (this.isMobile) {
        this.autoShowTooltip('fullscreenRoughTooltip', 1200);
        this.tooltipRefs.forEach(ref => {
          if (ref !== 'fullscreenRoughTooltip') {
            const t = this.$refs[ref];
            t?.handleClose?.();
          }
        });
      }
      this.question.roughPaperShow = true;
    },

    // ==================== 分割线拖动逻辑（Grid 布局优化版） ====================
    startResizeSplit(event) {
      event.preventDefault();
      if (this.isResizingSplit) return;

      // 获取起始坐标和当前左侧面板宽度
      let clientX;
      if (event.type === 'mousedown') {
        clientX = event.clientX;
        document.addEventListener('mousemove', this.onResizeMove);
        document.addEventListener('mouseup', this.stopResizeSplit);
      } else {
        clientX = event.touches[0].clientX;
        document.addEventListener('touchmove', this.onResizeMove, { passive: false });
        document.addEventListener('touchend', this.stopResizeSplit);
        document.addEventListener('touchcancel', this.stopResizeSplit);
      }

      // 获取容器总宽度
      const container = this.$refs.splitContainer;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      this.containerWidth = containerRect.width;

      this.startX = clientX;
      this.startWidth = this.leftPanelWidth;
      this.isResizingSplit = true;

      // 添加全局样式防止选择
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },

    onResizeMove(event) {
      if (!this.isResizingSplit) return;

      let clientX;
      if (event.type === 'mousemove') {
        clientX = event.clientX;
      } else {
        clientX = event.touches[0].clientX;
        event.preventDefault();
      }

      const deltaX = clientX - this.startX;
      const deltaPercent = (deltaX / this.containerWidth) * 100;
      let newWidth = this.startWidth + deltaPercent;

      // 移动端优化：允许左侧完全收起（最小0%），最大保持65%
      newWidth = Math.min(65, Math.max(0, newWidth));

      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => {
        this.leftPanelWidth = newWidth;
        this.rafId = null;
      });
    },

    stopResizeSplit() {
      if (!this.isResizingSplit) return;
      this.isResizingSplit = false;
      this.startX = 0;
      this.startWidth = 0;
      this.containerWidth = 0;

      // 移除全局事件监听
      document.removeEventListener('mousemove', this.onResizeMove);
      document.removeEventListener('mouseup', this.stopResizeSplit);
      document.removeEventListener('touchmove', this.onResizeMove);
      document.removeEventListener('touchend', this.stopResizeSplit);
      document.removeEventListener('touchcancel', this.stopResizeSplit);

      // 恢复样式
      document.body.style.userSelect = '';
      document.body.style.cursor = '';

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    },

    onSplitDialogOpened() {
      // 重置左侧面板宽度为默认值（可选，根据需要注释）
      // this.leftPanelWidth = 30;
      // 确保任何残留的拖动状态被清理
      this.stopResizeSplit();
      // 保证分割线在弹窗打开后事件绑定正常（模板已绑定，此处无需重复）
    },

    onSplitDialogClosed() {
      this.stopResizeSplit();
    },

    removeResizeListeners() {
      this.stopResizeSplit();
    },

    // 注意：showAnswer 和 recommandTopic 方法由父组件或 mixin 提供，这里仅作空实现避免报错
    showAnswer(question, level) {
      this.$emit('showAnswer', { question, level });
    },
    recommandTopic(question) {
      this.$emit('recommandTopic', question);
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep img {
  max-width: 30vw;
}
::v-deep li:has(.katex) {
  line-height: 3;
  font-size: 14px;
}
::v-deep img[data-image-type="base64"] {
  width: 80%;
}
::v-deep pre {
  max-width: clamp(200px, 100%, 60vw);
}

.exercises-item {
  max-width: calc(100vw - var(--robot-width) - var(--history-width) - 148px);
  width: 100%;
  word-break: break-word;

  .drag-handle {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $base_green;
    font-size: 20px;
    touch-action: none;
    cursor: move;
  }

  .user-answer {
    margin: 8px auto 0;
    padding: 0 15px 10px;
    width: 80%;
    border-radius: 8px;
    background-color: var(--collapse-bg-color);

    .user-answer-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 10px;

      .placeholder {
        width: 24px; // 与删除按钮同宽，保证标题居中
      }

      .title {
        text-align: center;
        font-size: 14px;
        font-weight: 600;
        color: rgb(124, 124, 124);
        margin: 0;
      }

      .delete-answer {
        font-size: 18px;
        color: #999;
        cursor: pointer;
        transition: color 0.2s;
        &:hover {
          color: #f56c6c;
        }
      }
    }

    ::v-deep p {
      margin-bottom: 5px;
      margin-top: 5px;
    }
    ::v-deep img {
      margin-bottom: -25px;
      max-height: 60px;
    }
  }

  .el-collapse {
    margin-bottom: 10px;
    padding-right: 0px;
    color: var(--txt-default-color);

    ::v-deep .el-collapse-item__wrap {
      position: relative;
      max-height: 50vh;
    }
    .collapse-wrapper {
      margin-right: 10px;
    }
    .teacher-expl {
      padding-right: 10px;
    }
    .view-answer {
      padding-right: 10px;
      overflow: scroll;
    }
    .standard-answer {
      padding-right: 10px;
      overflow: scroll;
    }
    ::v-deep .part-title {
      font-weight: 700;
    }
    ::v-deep .el-collapse-item__header {
      padding-left: 20px;
      border-radius: 10px 10px 0 0;
      background-color: var(--collapse-bg-color);
      text-align: center;
      font-size: 16px;
      font-weight: 600;
      color: rgb(124, 124, 124);
    }
    ::v-deep .el-collapse-item__content {
      padding-bottom: 0;
      line-height: inherit;
      color: var(--txt-default-color);
      table {
        margin: 0 auto;
      }
    }
    ::v-deep .el-collapse-item__wrap {
      overflow: auto;
      padding-top: 10px;
      padding-left: 20px;
      padding-right: 20px;
      padding-bottom: 15px;
      border-radius: 0 0 10px 10px;
      background-color: var(--collapse-bg-color);
      ol {
        padding-left: 1rem;
        li {
          flex: 100%;
        }
      }
      p {
        margin-top: 0;
        margin-bottom: 10px;
        line-height: 2;
        &:has(.katex) {
          line-height: 2.6;
        }
        &:has(img) {
          text-align: center;
        }
      }
    }
  }

  .title-section {
    position: relative;
    display: flex;
    align-items: middle;
    gap: 8px;

    .collect-question {
      margin-right: 8px;
      position: relative;
      width: 22px;
      height: 22px;
      margin-left: 10px;
      font-weight: 700;
      cursor: pointer;
      i {
        position: absolute;
        font-size: 22px;
        top: 20%;
      }
    }

    .exercises-title {
      font-size: 14px;
      flex-grow: 1;
      display: flex;
      align-items: baseline;
      max-width: calc(100vw - var(--robot-width) - var(--history-width) - 100px);
      width: 100%;
      ::v-deep li:has(.katex) {
        line-height: 1.8;
        font-size: 14px;
      }
      ::v-deep p:has(.katex) {
        line-height: 1.8;
      }
      .serial-number {
        line-height: 1.8;
        font-size: 14px;
        width: 28px;
        height: 20px;
      }
      .question {
        flex: 1;
        ::v-deep p {
          margin-left: 0;
          margin-bottom: 6px;
        }
      }
      ::v-deep ol {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      ::v-deep li {
        line-height: 2;
      }
      ::v-deep p {
        margin-left: 16px;
        margin-top: 0;
      }
    }
  }

  .option-wrap {
    margin-left: 60px;
    ::v-deep {
      img {
        width: 50% !important;
        max-width: 100%;
      }
    }
    ::v-deep li:has(.katex) {
      line-height: initial;
      font-size: 14px;
    }
    ::v-deep p:has(.katex) {
      line-height: initial;
    }
    ::v-deep .el-radio-group {
      display: flex;
      width: 100%;
      flex-wrap: wrap;
      .el-radio {
        display: flex;
        color: var(--txt-default-color);
        .el-radio__label {
          img {
            vertical-align: middle;
          }
        }
      }
    }
    .el-checkbox-group {
      .el-checkbox {
        color: var(--txt-default-color);
      }
    }
  }

  .flex-horizon {
    display: flex;
    flex-wrap: wrap;
    padding-right: 8px;
    .group {
      margin-top: 5px;
      display: flex;
      flex-wrap: wrap;
    }
    .el-checkbox,
    .el-radio {
      margin-bottom: 15px;
      padding-left: 20px;
      display: flex;
      align-items: center;
      ::v-deep p {
        margin: 0;
        overflow: hidden;
        width: fit-content;
      }
    }
  }

  ::v-deep .el-radio {
    p {
      margin: 10px 0;
    }
  }
}

::v-deep .el-dialog__body {
  padding: 0 20px 8px;
  .el-textarea__inner {
    margin: 0 auto;
    width: 95%;
    max-height: 420px;
  }
}

$font_size: $font_size;
.draw-panel {
  height: 100%;
  background-color: var(--leftbox-bg-color);
}

.isMobile {
  padding: 0 0 12px;
  ::v-deep li:has(.katex) {
    line-height: 2.5;
    font-size: 14px;
  }
}

.flex-horizon {
  .el-checkbox,
  .el-radio {
    margin-bottom: 5px;
  }
}

.roughPaperDialog {
  ::v-deep .el-dialog {
    margin-top: 10vh;
    overflow: hidden !important;
    max-width: 99vw;
  }
  ::v-deep .el-dialog__body {
    height: 84%;
  }
}

.exercises-title {
  ::v-deep ol {
    list-style: none;
  }
}

.custom-label {
  display: inline-block;
  max-width: 100%;
  height: 100%;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-all;
}

::v-deep .el-radio .el-radio__label {
  white-space: normal;
  word-wrap: break-word;
}

.answerDialog {
  ::v-deep .el-dialog {
    background: transparent !important;
    border: 2px solid #9dca67 !important;
    border-radius: 12px;
    box-shadow: 0 8px 20px rgba(6, 156, 243, 0.3);
  }
  ::v-deep .el-dialog__header,
  ::v-deep .el-dialog__body,
  ::v-deep .el-dialog__footer {
    background: transparent !important;
  }
  ::v-deep .el-dialog__title {
    color: rgb(123, 212, 6) !important;
    font-weight: 600;
    text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  }
  ::v-deep .el-dialog__headerbtn .el-dialog__close {
    color: rgb(236, 221, 6) !important;
    font-size: 24px;
    &:hover {
      color: rgb(236, 97, 4) !important;
    }
  }
  ::v-deep .dialog-footer button {
    background: rgba(0, 0, 0, 0.6) !important;
    border: 1px solid #9dca67 !important;
    color: #d4ff9d !important;
    padding: 8px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
    &:hover {
      background: rgba(0, 0, 0, 0.8) !important;
      border-color: #ffd966 !important;
      color: #ffd966 !important;
    }
  }
  ::v-deep .el-dialog__body {
    color: #e0ffc0 !important;
  }
  &.is-fullscreen {
    ::v-deep .el-dialog {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
      margin: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      max-height: 100vh !important;
      border-radius: 0 !important;
    }
    ::v-deep .el-dialog__header,
    ::v-deep .el-dialog__footer {
      display: none !important;
    }
    ::v-deep .el-dialog__body {
      padding: 0 !important;
      height: 100vh !important;
      overflow: hidden;
    }
    .math-container,
    .editor,
    .w-e-text-container,
    [data-slate-editor] {
      height: 100% !important;
      min-height: 100vh !important;
    }
  }
  &.is-fullscreen {
    ::v-deep .el-dialog {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
      margin: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      max-height: 100vh !important;
      border-radius: 0 !important;
    }
    ::v-deep .el-dialog__header {
      display: none !important;
    }
    ::v-deep .el-dialog__footer {
      display: block !important;
      position: fixed;
      bottom: 20px;
      right: 20px;
      left: auto;
      top: auto;
      background: rgba(0, 0, 0, 0.7);
      border-radius: 8px;
      padding: 8px 16px;
      z-index: 2100;
      border: 1px solid #9dca67;
      box-shadow: 0 2px 12px rgba(0,0,0,0.3);
      .dialog-footer {
        button {
          margin: 0 4px;
          font-size: 14px;
          padding: 6px 16px;
        }
      }
    }
    ::v-deep .el-dialog__body {
      padding-bottom: 70px !important;
      height: 100vh !important;
      box-sizing: border-box;
    }
    .math-container,
    .editor,
    .w-e-text-container,
    [data-slate-editor] {
      height: 100% !important;
      min-height: 100vh !important;
    }
  }
}

.roughPaperDialog {
  ::v-deep .el-dialog {
    background: transparent !important;
    border: 2px solid #9dca67 !important;
    border-radius: 12px;
    box-shadow: 0 8px 20px rgba(6, 156, 243, 0.3);
  }
  ::v-deep .el-dialog__header,
  ::v-deep .el-dialog__body,
  ::v-deep .el-dialog__footer {
    background: transparent !important;
  }
  .rough-paper-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    .draft-icon {
      font-size: 20px;
      color: #9dca67;
      transition: all 0.25s ease;
      display: inline-block;
    }
    span {
      color: rgb(4, 204, 226) !important;
      font-weight: 600;
      text-shadow: 0 0 2px rgba(247, 230, 154, 0.5);
    }
  }
  ::v-deep .el-dialog__title:hover .draft-icon {
    transform: scale(1.15);
    color: #ffd966 !important;
  }
  ::v-deep .el-dialog__headerbtn .el-dialog__close {
    color: rgb(250, 100, 62) !important;
    font-size: 24px;
    &:hover {
      color: rgb(247, 3, 15) !important;
    }
  }
  ::v-deep .dialog-footer button {
    background: rgba(0, 0, 0, 0.6) !important;
    border: 1px solid #9dca67 !important;
    color: #d4ff9d !important;
    padding: 8px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
    &:hover {
      background: rgba(0, 0, 0, 0.8) !important;
      border-color: #ffd966 !important;
      color: #ffd966 !important;
    }
  }
  ::v-deep .draw-panel {
    background: transparent !important;
  }
  &.is-fullscreen {
    ::v-deep .el-dialog {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
      margin: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      max-height: 100vh !important;
      border-radius: 0 !important;
    }
    ::v-deep .el-dialog__header,
    ::v-deep .el-dialog__footer {
      display: none !important;
    }
    ::v-deep .el-dialog__body {
      padding: 0 !important;
      height: 100vh !important;
    }
    ::v-deep .draw-panel {
      height: 100%;
      border-radius: 0;
    }
  }
}

/* 老师讲解全屏分屏样式（使用 Grid 布局） */
.teacher-split-dialog {
  ::v-deep .el-dialog {
    background: var(--leftbox-bg-color, #fff) !important;
    color: var(--txt-default-color, #333);
  }
  ::v-deep .el-dialog__header {
    border-bottom: 1px solid var(--border-color, #eee);
    padding: 16px 20px;
  }
  ::v-deep .el-dialog__title {
    color: var(--txt-default-color, #333);
    font-weight: 600;
  }
  ::v-deep .el-dialog__headerbtn .el-dialog__close {
    color: rgb(235, 173, 5) !important;
    font-size: 24px;
    transition: color 0.2s;
    &:hover {
      color: rgb(170, 83, 2) !important;
    }
  }
  ::v-deep .el-dialog__body {
    padding: 0 !important;
    height: calc(100vh - 54px);
    overflow: hidden;
  }
  ::v-deep .el-dialog__footer {
    border-top: 1px solid var(--border-color, #eee);
    text-align: right;
    padding: 12px 20px;
    background: var(--leftbox-bg-color, #fff);
  }
  .split-container {
    display: grid;
    height: 100%;
    overflow: hidden;
    position: relative;
  }
  .left-panel {
    background: var(--leftbox-bg-color, #f5f5f5);
    overflow-y: auto;
    padding: 16px;
    box-sizing: border-box;
    .exercises-item {
      max-width: 100%;
    }
  }
  .resize-handle {
    width: 6px;
    background-color: #ccc;
    cursor: col-resize;
    transition: background-color 0.2s;
    &:hover {
      background-color: #9dca67;
    }
    &:active {
      background-color: #6c9c3a;
    }
  }
  .right-panel {
    background: var(--rightbox-bg-color, #fff);
    overflow-y: auto;
    padding: 16px 24px;
    box-sizing: border-box;
    .teacher-content {
      ::v-deep img {
        max-width: 80%;
        margin: 0 auto;
        display: block;
      }
      ::v-deep p {
        line-height: 1.6;
      }
    }
  }
  .fullscreen-close-btn {
    background: rgba(0, 0, 0, 0.6) !important;
    border: 1px solid #9dca67 !important;
    color: #9dca67 !important;
    padding: 8px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
    &:hover {
      background: rgba(0, 0, 0, 0.8) !important;
      border-color: #c5e0b4 !important;
      color: #c5e0b4 !important;
    }
  }
  // 底部 footer 
  .dialog-footer.teacher-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    // 移除原有的 .footer-left 相关样式，不再需要
    .fullscreen-close-btn {
      background: rgba(0, 0, 0, 0.6);
      border: 1px solid #9dca67;
      color: #9dca67;
      padding: 8px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 500;
      transition: all 0.2s;
      &:hover {
        background: rgba(0, 0, 0, 0.8);
        border-color: #c5e0b4;
        color: #c5e0b4;
      }
    }
  }
  // 新增：标题栏左侧布局
  .split-dialog-title {
    .title-left {
      display: flex;
      align-items: center;
      gap: 8px;

      .toolbar-icon {
        font-size: 20px;
        color: #20b2aa;
        cursor: pointer;
        transition: all 0.2s;
        &:hover {
          color: #2e8b57;
          transform: scale(1.05);
        }
      }
      span {
        font-weight: 600;
      }
      // ✅ 展开按钮样式放在这里
      .expand-question-btn {
        margin-left: 12px;
        font-size: 18px;
        color: #9dca67;
        cursor: pointer;
        transition: all 0.2s;
        &:hover {
          color: #ffd966;
          transform: scale(1.1);
        }
      }
    }
  }
  .left-panel {
    background: var(--leftbox-bg-color, #f5f5f5);
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px;
    box-sizing: border-box;
    min-width: 0;
  }

  .right-panel {
    background: var(--rightbox-bg-color, #fff);
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px 24px;
    box-sizing: border-box;
    min-width: 0;
  }

  // 移动端增大分割线触摸区域
  @media (max-width: 768px) {
    .resize-handle {
      width: 6px;
      background-color: rgba(185, 239, 248, 0.8);
    }
  }
}

/* 全屏图标样式 - 亮绿色 */
.fullscreen-teacher-icon {
  margin-left: 12px;
  cursor: pointer;
  font-size: 16px;
  color:rgb(2, 194, 168);
  transition: color 0.2s;
  &:hover {
    color:rgb(1, 128, 145);
  }
}

/* 自定义弹窗标题样式 */
.answer-dialog-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  span {
    color:rgb(143, 248, 5);
    font-weight: 600;
  }
  .photo-upload-btn {
    background: rgba(0, 0, 0, 0.6);
    border: 1px solid #9dca67;
    color: #d4ff9d;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    cursor: pointer;
    &:hover {
      background: rgba(0, 0, 0, 0.8);
      border-color: #ffd966;
      color: #ffd966;
    }
  }
}

/* 动作面板样式 */
.action-sheet-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(246, 253, 253, 0.8);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.action-sheet {
  width: 100%;
  background: rgb(229, 243, 220);
  border-radius: 16px 16px 0 0;
  padding: 10px 0;
  color: rgb(108, 6, 241);
}
.action-sheet-item {
  padding: 15px 0;
  text-align: center;
  font-size: 16px;
  border-bottom: 2px solid rgb(175, 230, 243);
  cursor: pointer;
}
.action-sheet-item.cancel {
  border-bottom: none;
  color: #999;
}

/* 相机界面样式 */
.camera-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.camera-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.camera-video {
  flex: 1;
  width: 100%;
  object-fit: cover;
}
.crop-box {
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}
.crop-area {
  width: 100%;
  height: 100%;
  background: transparent;
  pointer-events: none;
}
.crop-hint {
  color: white;
  font-size: 14px;
  text-align: center;
  line-height: 1.2;
  padding: 5px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  z-index: 1;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  white-space: nowrap;
}
.camera-container .resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50% 0 50% 0;
  cursor: nwse-resize;
  pointer-events: auto;
  z-index: 2;
}
.camera-footer {
  position: absolute;
  bottom: 30px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-around;
  padding: 0 20px;
  box-sizing: border-box;
}
.btn-cancel,
.btn-capture,
.btn-album {
  padding: 12px 30px;
  border-radius: 30px;
  border: none;
  font-size: 16px;
  background: white;
  color: #333;
  cursor: pointer;
}
.btn-capture {
  background: rgb(108, 6, 241);
  color: white;
}
.btn-album {
  background: #fff;
  color: rgb(108, 6, 241);
}

@media (max-width: 1024px) {
  ::v-deep p {
    overflow: auto;
  }
  ::v-deep img {
    max-width: 60vw;
  }
  ::v-deep img[data-image-type="base64"] {
    width: 100%;
  }
  ::v-deep .el-collapse-item__wrap {
    padding-right: 5px !important;
    padding-left: 8px !important;
    padding-top: 0 !important;
    max-height: 40vh;
    p:has(.katex) {
      line-height: 2.8 !important;
    }
    .teacher-expl,
    .view-answer,
    .standard-answer {
      padding-right: 0 !important;
    }
  }
  ::v-deep ul {
    list-style: none !important;
    padding: 0;
  }
  * {
    font-size: $font_size;
  }
  ::v-deep img {
    max-width: 100%;
  }
  ::v-deep li {
    font-size: $font_size;
  }
  .exercises-item {
    max-width: calc(100vw - 20px) !important;
    .title-section {
      .exercises-title {
        font-size: $font_size !important;
        max-width: 100% !important;
        ::v-deep p {
          margin-left: 5px;
          margin-bottom: 0;
          line-height: 2;
        }
        .question {
          width: 90%;
        }
      }
    }
    .collect-question {
      margin-right: 0 !important;
      margin-left: 0 !important;
      i {
        font-size: 0.5rem important;
      }
    }
    .option-wrap {
      margin-left: 0 !important;
      margin: 0 auto;
      padding-left: 10px;
      .el-checkbox-group,
      .el-radio-group {
        width: 100%;
      }
      ::v-deep .katex {
        line-height: 2;
      }
      ::v-deep p {
        margin: 5px 0;
      }
      ::v-deep img {
        width: 80%;
      }
    }
    .el-radio {
      margin-right: 12px;
      ::v-deep .el-radio__label {
        padding-left: 2px;
      }
    }
  }
  ::v-deep .markdown-body * {
    font-size: 0.22rem !important;
  }
  .exercises-item {
    margin-top: 0 !important;
    align-items: baseline;
    .title-section {
      .exercises-title {
        .serial-number {
          width: auto;
          padding-right: 5px;
        }
      }
    }
  }
  .el-collapse {
    ::v-deep .el-collapse-item__header {
      font-size: 0.22rem;
      min-width: 40vw;
    }
    ::v-deep .el-collapse-item__wrap {
      ol {
        padding: 0;
        padding-left: 20px !important;
        margin: 0;
        li {
          padding: 0;
          margin: 0;
        }
      }
      p {
        padding-left: 10px;
        img {
          width: 100%;
          height: 100%;
        }
      }
      img {
        width: 100%;
        height: 100%;
      }
    }
  }
  .answerDialog {
    overflow: hidden;
    ::v-deep .el-dialog {
      margin-top: 4vh !important;
      width: 92vw;
      max-height: 86vh;
      .el-dialog__body {
        height: fit-content;
      }
      .el-dialog__footer {
        button {
          font-size: 12px !important;
        }
      }
      * {
        font-size: clamp(10px, 14px, 16px);
      }
    }
    &:has(.w-e-full-screen-container) {
      width: 100%;
      height: 100%;
      margin-top: 0 !important;
      transform: scale(1);
    }
  }
  .roughPaperDialog {
    overflow: hidden !important;
    margin-top: 0;
    ::v-deep .el-dialog {
      margin-top: 2vh !important;
      min-width: 92vw;
      min-height: 77vh;
      .el-dialog__body {
        height: 75vh;
      }
      canvas {
        height: 70vh;
      }
      &:has(.isFull) {
        width: 100vw !important;
        max-width: 100vw !important;
        height: 100vh !important;
        max-height: 100vh !important;
        margin-top: 0 !important;
        transform: scale(1);
        canvas {
          width: 100%;
          height: 100vh !important;
        }
      }
    }
  }
}

@media (max-width: 750px) {
  .exercises-item {}
}
</style>